import { MusicEntry, SfxEntry } from '../types/audio.types';

// ---------------------------------------------------------------------------
// Music
// ---------------------------------------------------------------------------

export const MUSIC_REGISTRY: Record<string, MusicEntry> = {
  default: { src: '/music/default.opus' },
  'lively-city': { src: '/music/lively-city.opus' },
  'royal-castle': { src: '/music/royal-castle.opus' },
  'peaceful-village': { src: '/music/peaceful-village.opus' },
  'long-journey': { src: '/music/long-journey.opus' },
  'hidden-cavern': { src: '/music/hidden-cavern.opus' },
  'spirits-forest': { src: '/music/spirits-forest.opus' },
  'wood-forest-town': { src: '/music/wood-forest-town.opus' },
  'dwarven-mine': { src: '/music/dwarven-mine.opus' },
  'dangerous-cave': { src: '/music/dangerous-cave.opus' },
  'goofy-monster': { src: '/music/goofy-monster.opus' },
  'magic-temple': { src: '/music/magic-temple.opus' },
  'traveling-the-sky': { src: '/music/traveling-the-sky.opus' },
  'volcanic-crater': { src: '/music/volcanic-crater.opus' },
  'unknown-island': { src: '/music/unknown-island.opus' },
  'the-old-magician': { src: '/music/the-old-magician.opus' },
  'east-town': { src: '/music/east-town.opus' },
  'military-base': { src: '/music/military-base.opus' },
  'malicious-scheme': { src: '/music/malicious-scheme.opus' },
  'ancient-library': { src: '/music/ancient-library.opus' },
  pyramid: { src: '/music/pyramid.opus' },
  'dark-factory': { src: '/music/dark-factory.opus' },
  'demon-king-castle': { src: '/music/demon-king-castle.opus' },
  'the-evil-one': { src: '/music/the-evil-one.opus' },
  'holy-sanctuary': { src: '/music/holy-sanctuary.opus' },
};

export type MusicId = keyof typeof MUSIC_REGISTRY;

// ---------------------------------------------------------------------------
// Sound effects (SFX)
// ---------------------------------------------------------------------------

// Curated subset of the SFX library: each sound is a pool of 6 variants
// (`<family>_HY_PC-001..006.opus`). Spaces are percent-encoded for fetch().
const sfxPool = (family: string, volume?: number): SfxEntry => ({
  pool: Array.from(
    { length: 6 },
    (_, i) =>
      `/sfx/${family.replaceAll(' ', '%20')}_HY_PC-${String(i + 1).padStart(3, '0')}.opus`,
  ),
  ...(volume !== undefined ? { volume } : {}),
});

export const SFX_REGISTRY: Record<string, SfxEntry> = {
  // --- Spell impacts by emotion (played on cast, derived from element) ---
  'spell-joy': sfxPool('DSGNImpt_EXPLOSION-Flare Nova', 0.6),
  'spell-sadness': sfxPool('DSGNImpt_EXPLOSION-Grainy Burst', 0.6),
  'spell-anger': sfxPool('DSGNImpt_EXPLOSION-Fire Hit', 0.6),
  'spell-fear': sfxPool('DSGNImpt_EXPLOSION-Voltaic Blast', 0.6),
  'spell-surprise': sfxPool('DSGNImpt_EXPLOSION-Laser Impact', 0.6),
  'spell-disgust': sfxPool('DSGNImpt_EXPLOSION-Crunching', 0.6),

  // --- Events ---
  levelup: sfxPool('MAGAngl_BUFF-Effect Success'),
  'learn-spell': sfxPool('MAGSpel_CAST-Skill Ready'),
  buy: sfxPool('DSGNTonl_USABLE-Coin Spend'),
  'item-use': sfxPool('DSGNTonl_USABLE-Generic Consume'),
  'trap-spike': sfxPool('DSGNImpt_EXPLOSION-Thud', 0.7),
  'trap-warp': sfxPool('WHSH_MOVEMENT-Phase Sweeps', 0.7),
  'trap-alarm': sfxPool('UIMisc_INTERFACE-Denied', 0.7),

  // --- POI interactions ---
  chest: { src: '/sfx/chest.opus' },
  urn: { src: '/sfx/urn.opus' },

  // --- UI / interface (played centrally by useInputContext on 'interface') ---
  'ui-cursor': { src: '/sfx/cursor_style_5.opus', volume: 0.45 },
  'ui-confirm': { src: '/sfx/confirm_style_5_001.opus', volume: 0.6 },
  'ui-back': { src: '/sfx/back_style_5_001.opus', volume: 0.6 },
  'ui-error': { src: '/sfx/error_style_5_001.opus', volume: 0.6 },
};

export type SfxId = keyof typeof SFX_REGISTRY;
